import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Plus, X, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { useEditableSettings } from '@/hooks/useEditableSettings';
import { EditableText } from '@/components/EditableText';
import { SiteNav } from '@/components/SiteNav';
import { API } from '@/App';

const About = () => {
  const { settings, isAdmin, queueSave, updateField, updateLabel, setUser } = useEditableSettings();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  if (!settings) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-pulse text-[11px] tracking-[0.3em] uppercase text-[#525252]">Loading</div></div>;
  }

  const L = settings.labels || {};
  const paragraphs = settings.about_paragraphs || [];
  const aboutImage = settings.about_image
    ? (settings.about_image.startsWith('http') ? settings.about_image : `${API}/files/${settings.about_image}`)
    : null;

  const updateParagraph = (i, value) => {
    const next = [...paragraphs];
    next[i] = value;
    queueSave({ ...settings, about_paragraphs: next });
  };
  const addParagraph = () => queueSave({ ...settings, about_paragraphs: [...paragraphs, ''] });
  const removeParagraph = (i) => queueSave({ ...settings, about_paragraphs: paragraphs.filter((_, j) => j !== i) });

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('session_token');
      const form = new FormData();
      form.append('file', file);
      const up = await axios.post(`${API}/files/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      // Persist immediately (no debounce) so a quick refresh doesn't lose it
      await axios.put(`${API}/settings`, { ...settings, about_image: up.data.path }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateField('about_image', up.data.path);
      toast.success('Photo added');
    } catch (e) {
      toast.error('Upload failed');
    }
    setUploading(false);
  };

  const removeImage = async () => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/settings`, { ...settings, about_image: '' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateField('about_image', '');
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="about-page">
      <SiteNav settings={settings} isAdmin={isAdmin} setUser={setUser} updateField={updateField} updateLabel={updateLabel} active="about" />

      <div className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto">
          {isAdmin && (
            <div className="mb-10 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
              Inline editing on — changes save automatically
            </div>
          )}

          <EditableText
            as="h2"
            value={L.about_title ?? 'About'}
            onChange={(v) => updateLabel('about_title', v)}
            editable={isAdmin}
            placeholder="Page title"
            className="text-[11px] tracking-[0.2em] uppercase font-bold mb-10 sm:mb-14 block"
            testId="about-title"
          />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            {/* Photo column */}
            <div className="md:col-span-5">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="about-image-input"
              />
              {aboutImage ? (
                <div className="relative group">
                  <img src={aboutImage} alt="About" className="w-full h-auto" data-testid="about-image" />
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute bottom-2 left-2 bg-white border border-[#E5E5E5] hover:border-black px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-semibold flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="replace-about-image"
                      >
                        <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {uploading ? 'Uploading…' : 'Replace'}
                      </button>
                      <button
                        onClick={removeImage}
                        className="absolute top-2 right-2 bg-white border border-[#E5E5E5] hover:border-black p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove photo"
                        data-testid="remove-about-image"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </div>
              ) : isAdmin ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full aspect-[3/4] border border-dashed border-[#A3A3A3] hover:border-black bg-white/40 hover:bg-white flex flex-col items-center justify-center gap-3 text-[#525252] hover:text-black transition-colors"
                  data-testid="add-about-image"
                >
                  <ImagePlus className="w-8 h-8" strokeWidth={1} />
                  <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">
                    {uploading ? 'Uploading…' : 'Add photo'}
                  </span>
                </button>
              ) : null}
            </div>

            {/* Text column */}
            <div className={aboutImage || isAdmin ? 'md:col-span-7' : 'md:col-span-12 max-w-2xl mx-auto'}>
              <div className="space-y-6">
                {paragraphs.map((para, i) => (
                  <div key={i} className="group relative">
                    <EditableText
                      value={para}
                      onChange={(v) => updateParagraph(i, v)}
                      editable={isAdmin}
                      multiline
                      placeholder="Paragraph text…"
                      className="block text-[13px] sm:text-sm leading-[1.9] tracking-[0.02em] text-[#262626]"
                      testId={`about-paragraph-${i}`}
                    />
                    {isAdmin && (
                      <button
                        onClick={() => removeParagraph(i)}
                        className="opacity-0 group-hover:opacity-100 absolute -right-7 top-0 text-[#A3A3A3] hover:text-black transition-opacity"
                        title="Remove paragraph"
                        data-testid={`remove-paragraph-${i}`}
                      >
                        <X className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}
                {isAdmin && (
                  <button
                    onClick={addParagraph}
                    className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#A3A3A3] hover:text-black flex items-center gap-1.5"
                    data-testid="add-paragraph"
                  >
                    <Plus className="w-3 h-3" strokeWidth={2} /> Add paragraph
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
